import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, XCircle, SpinnerGap } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PREORDER_MODE } from "@/lib/siteConfig";
import { fbTrack } from "@/lib/fbpixel";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear, total, items } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { fbTrack("Purchase", { value: total || 0, currency: "USD", content_type: "product", content_ids: items.map((i) => i.product_id) }); clear(); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { console.debug("payment status poll retry", e); }
      tries.current += 1;
      if (tries.current >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, clear]);

  return (
    <div className="max-w-xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <SpinnerGap size={56} className="text-primary mx-auto mb-6 animate-spin" />
          <h1 className="text-2xl font-modern font-light">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-2">Hang tight, this only takes a second.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle weight="fill" size={64} className="text-primary mx-auto mb-6" data-testid="payment-paid" />
          <h1 className="text-3xl font-modern font-light mb-3">{PREORDER_MODE ? "Pre-order confirmed 🐾" : "Payment confirmed 🐾"}</h1>
          <p className="text-muted-foreground mb-8">{PREORDER_MODE ? "You're locked in. Production is finishing up — we'll email you tracking the moment your pieces ship." : "Your Wigglez order is locked in. Thank you for repping the pack."}</p>
          <button onClick={() => navigate("/account")} className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="payment-view-orders">View my orders</button>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <XCircle weight="fill" size={64} className="text-accent mx-auto mb-6" />
          <h1 className="text-2xl font-modern font-light mb-3">We couldn't confirm payment yet</h1>
          <p className="text-muted-foreground mb-8">If you completed checkout, your order will appear in your account shortly.</p>
          <Link to="/account" className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors">Go to my account</Link>
        </>
      )}
    </div>
  );
}
